/**
 * Ensure a date string is treated as UTC.
 * If it lacks timezone info (no Z, no +/-offset), append Z.
 */
function ensureUTC(dateStr) {
  if (!dateStr) return dateStr;
  const s = String(dateStr).trim();
  // Already has timezone indicator
  if (s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s) || /[+-]\d{4}$/.test(s)) return s;
  // Append Z to treat as UTC
  return s + 'Z';
}

/**
 * Format a date string (UTC) to the entity's timezone.
 * @param {string} dateStr - ISO date string (UTC)
 * @param {string} tz - IANA timezone (e.g., 'Europe/Madrid')
 * @param {object} opts - Intl.DateTimeFormat options override
 * @returns {string} Formatted date string
 */
export function formatDateTz(dateStr, tz, opts = {}) {
  if (!dateStr) return "-";
  try {
    const date = new Date(ensureUTC(dateStr));
    if (isNaN(date.getTime())) return dateStr;
    const defaultOpts = {
      timeZone: tz || "Europe/Madrid",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      ...opts,
    };
    return date.toLocaleDateString("ca-ES", defaultOpts);
  } catch {
    return dateStr;
  }
}

/**
 * Format a datetime string with time to the entity's timezone.
 */
export function formatDateTimeTz(dateStr, tz, opts = {}) {
  if (!dateStr) return "-";
  try {
    const date = new Date(ensureUTC(dateStr));
    if (isNaN(date.getTime())) return dateStr;
    const defaultOpts = {
      timeZone: tz || "Europe/Madrid",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      ...opts,
    };
    return date.toLocaleString("ca-ES", defaultOpts);
  } catch {
    return dateStr;
  }
}

/**
 * Format only time in entity timezone.
 */
export function formatTimeTz(dateStr, tz) {
  if (!dateStr) return "-";
  try {
    const date = new Date(ensureUTC(dateStr));
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleTimeString("ca-ES", {
      timeZone: tz || "Europe/Madrid",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return dateStr;
  }
}

/**
 * Convert a UTC date to a Date object in the entity timezone.
 * Useful for charts and calculations.
 */
export function toEntityDate(dateStr, tz) {
  if (!dateStr) return null;
  try {
    return new Date(ensureUTC(dateStr));
  } catch {
    return null;
  }
}

/**
 * Format a date string that may be LOCAL or UTC.
 * - If has 'Z' or offset → UTC date, convert using entity timezone
 * - If no timezone info → LOCAL date, display as-is
 * @param {string} dateStr - ISO date string
 * @param {string} tz - IANA timezone (only used for UTC dates)
 * @returns {string} Formatted date string "DD/MM/YYYY HH:MM"
 */
export function formatLocalDateTime(dateStr, tz, opts = {}) {
  if (!dateStr) return "-";
  try {
    const s = String(dateStr).trim();
    const isUTC = s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s);

    if (isUTC && tz) {
      // UTC date - convert to entity timezone
      const date = new Date(s);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleString("ca-ES", {
        timeZone: tz,
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: false,
        ...opts
      });
    }

    // Local date - parse and format directly (no timezone conversion)
    const clean = s.replace('Z', '');
    const parts = clean.split('T');
    if (parts.length < 2) return dateStr;
    const [year, month, day] = parts[0].split('-');
    const [hour, minute] = parts[1].split(':');
    if (opts.dateOnly) return `${(day||'').padStart(2,'0')}/${(month||'').padStart(2,'0')}/${year}`;
    if (opts.timeOnly) return `${(hour||'00').padStart(2,'0')}:${(minute||'00').padStart(2,'0')}`;
    return `${(day||'').padStart(2,'0')}/${(month||'').padStart(2,'0')}/${year} ${(hour||'00').padStart(2,'0')}:${(minute||'00').padStart(2,'0')}`;
  } catch {
    return dateStr;
  }
}
